import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "contexts";
import { ExceptionHandlingType } from "types/apiServiceTypes";

export type SuspiciousLoginItem = {
  ip: string;
  uniqueUsersCount: number;
  totalLoginsCount: number;
  sameUserAgentsCount: number;
  suspicionScore: number;
  isWhiteListed: boolean;
  isBlackListed: boolean;
};

export type SuspiciousLoginsListResponse = {
  totalCount: number;
  items: SuspiciousLoginItem[];
};

interface UseSuspiciousLoginsQueryParams {
  skip: number;
  take: number;
  search?: string;
  withBlocked?: boolean;
  ignoreWhitelist?: boolean;
  withoutBannedUsers?: boolean;
}

export const useSuspiciousLoginsQuery = ({
  skip,
  take,
  search,
  withBlocked,
  ignoreWhitelist,
  withoutBannedUsers,
}: UseSuspiciousLoginsQueryParams) => {
  const { accessFetch } = useAppContext();

  return useQuery<SuspiciousLoginsListResponse>({
    queryKey: [
      "suspicious-logins",
      skip,
      take,
      search,
      withBlocked,
      ignoreWhitelist,
      withoutBannedUsers,
    ],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({
          skip: skip.toString(),
          take: take.toString(),
          ...(search && { search }),
          ...(withBlocked !== undefined && { withBlocked: withBlocked.toString() }),
          ...(ignoreWhitelist !== undefined && { ignoreWhitelist: ignoreWhitelist.toString() }),
          ...(withoutBannedUsers !== undefined && {
            withoutBannedUsers: withoutBannedUsers.toString(),
          }),
        });

        const response = await accessFetch(
          `/user-login/suspicious-logins?${params.toString()}`,
          {
            method: "GET",
          },
          ExceptionHandlingType.AUTOMATIC
        );

        const data = await response?.json();

        if (!response?.ok) {
          return Promise.reject(data);
        }

        return data;
      } catch (error) {
        console.error("Error fetching suspicious logins:", error);
        return { totalCount: 0, items: [] };
      }
    },
    initialData: { totalCount: 0, items: [] },
    placeholderData: () => ({ totalCount: 0, items: [] }),
    retry: false,
  });
};
